import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import { FormField } from '../../../components/app';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { CommissionsDialogProps } from '../types';

/** «298…, 202…» → список счетов; null — в списке есть не-номер счёта. */
function parseAccounts(raw: string): string[] | null {
  const list = raw.split(/[\s,;]+/).filter(Boolean);
  return list.every((a) => /^\d{10,32}$/.test(a)) ? list : null;
}

/**
 * Ставки комиссии по провайдерам. Ставка одна на провайдера и применяется ко
 * всей истории — история ставок не хранится (см. ADR 0019).
 */
export function CommissionsDialog({
  isOpen,
  onOpenChange,
  providers,
  onUpdate,
}: CommissionsDialogProps) {
  const [drafts, setDrafts] = useState<Record<string, { percent: string; from: string; accounts: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDrafts(
      Object.fromEntries(
        providers.map((p) => [
          p.id,
          {
            percent: String(p.commission_percent),
            from: p.commission_from || '',
            accounts: (p.sender_accounts ?? []).join(', '),
          },
        ]),
      ),
    );
  }, [isOpen, providers]);

  const isDirty = (provider: CommissionsDialogProps['providers'][number]) => {
    const draft = drafts[provider.id];
    if (!draft) return false;
    return (
      draft.percent !== String(provider.commission_percent) ||
      draft.from !== (provider.commission_from || '') ||
      draft.accounts !== (provider.sender_accounts ?? []).join(', ')
    );
  };

  const handleSave = async (id: string) => {
    const draft = drafts[id];
    const value = parseFloat(draft?.percent ?? '');
    if (Number.isNaN(value) || value < 0 || value > 100) return;
    const senderAccounts = parseAccounts(draft.accounts);
    if (senderAccounts === null) return;
    setSavingId(id);
    try {
      await onUpdate(id, { commissionPercent: value, commissionFrom: draft.from || null, senderAccounts });
    } finally {
      setSavingId(null);
    }
  };

  const setDraft = (id: string, patch: Partial<{ percent: string; from: string; accounts: string }>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0">
        <DialogHeader bordered>
          <DialogTitle>Ставки комиссии</DialogTitle>
          <DialogDescription>
            Ставка одна на провайдера и применяется ко всей истории. Если по договору был Льготный период —
            укажите дату, с которой провайдер начал удерживать комиссию: платежи раньше неё входят в Чистое
            целиком. Счета отправителя — с них провайдер зачисляет деньги; по ним Импорт выписки узнаёт его
            строки.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="gap-3">
          {providers.map((provider) => {
            const draft = drafts[provider.id];
            const percentValue = parseFloat(draft?.percent ?? '');
            const percentError =
              draft && (Number.isNaN(percentValue) || percentValue < 0 || percentValue > 100)
                ? 'От 0 до 100'
                : undefined;
            const accountsError =
              draft && parseAccounts(draft.accounts ?? '') === null
                ? 'Только номера счетов: 10–32 цифры через запятую'
                : undefined;
            const switchId = `cf-active-${provider.id}`;
            return (
              <section
                key={provider.id}
                aria-label={provider.name}
                className="flex flex-col gap-3 rounded-xl border border-border-soft bg-surface-alt p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-col">
                    <b className="font-display text-[15px]">{provider.name}</b>
                    <span className="font-mono text-xs text-text-2">{provider.code}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={switchId} tone="filter">
                      Активен
                    </Label>
                    <Switch
                      id={switchId}
                      checked={provider.is_active}
                      onCheckedChange={(checked) => onUpdate(provider.id, { isActive: checked })}
                    />
                  </div>
                </div>

                <div className="grid items-start gap-3 sm:grid-cols-[120px_180px_1fr]">
                  <FormField label="Ставка, %" tone="filter" error={percentError}>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      inputMode="decimal"
                      value={draft?.percent ?? ''}
                      onChange={(e) => setDraft(provider.id, { percent: e.target.value })}
                    />
                  </FormField>
                  <FormField label="Действует с" tone="filter" hint="Пусто — на всю историю">
                    <Input
                      type="date"
                      value={draft?.from ?? ''}
                      onChange={(e) => setDraft(provider.id, { from: e.target.value })}
                    />
                  </FormField>
                  <div className="flex h-full items-end justify-end pb-px sm:pt-[22px]">
                    <Button
                      size="sm"
                      onClick={() => handleSave(provider.id)}
                      disabled={savingId === provider.id || !isDirty(provider) || Boolean(percentError || accountsError)}
                    >
                      {savingId === provider.id && <Loader2 className="animate-spin" />}
                      Сохранить
                    </Button>
                  </div>
                </div>

                {provider.currency === 'UZS' && (
                  <FormField label="Счета отправителя" tone="filter" hint="Через запятую" error={accountsError}>
                    <Input
                      value={draft?.accounts ?? ''}
                      placeholder="Например: 29896000804942713020"
                      className="font-mono text-xs"
                      onChange={(e) => setDraft(provider.id, { accounts: e.target.value })}
                    />
                  </FormField>
                )}
              </section>
            );
          })}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
